import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface TrajectstapPubliek {
  id: string;
  status: string; // pending | active | completed
  volgorde: number;
  geplandeDatum: string | null;
  afgerondDatum: string | null;
  template: {
    naam: string; // JSON: {nl, fr, en}
    icon: string;
    kleur: string;
  };
}

interface ApparaatPubliek {
  id: string;
  naam: string;
  merk: string | null;
  status: string;
  binnenDatum: string | null;
}

interface DocumentPubliek {
  id: string;
  naam: string;
  documentType: string | null;
  mimeType: string;
  createdAt: string;
  status?: string | null;
  ondertekendOp?: string | null;
}

/** Basispad van de dossier-API voor een portaallink. De hooks hieronder kennen enkel dit pad. */
export const portaalApi = (token: string) => `/api/klantportaal/${encodeURIComponent(token)}`;

/** Fout met HTTP-status, zodat de pagina "link ongeldig/verlopen" (404) kan onderscheiden van "tijdelijk niet bereikbaar". */
export class PortaalFout extends Error {
  constructor(message: string, public status: number | null) {
    super(message);
  }
}

export interface BerichtPubliek {
  id: string;
  content: string;
  senderType: string; // customer | agent | system | field_worker
  channel: string;
  createdAt: string;
  readAt: string | null;
  milestoneId: string | null;
  sender?: { firstName: string; lastName: string } | null;
  /** Enkel via een klantaccount: wie van de deelnemers schreef, en of dat jijzelf was. */
  afzenderNaam?: string | null;
  vanMij?: boolean;
  attachments: { id: string; filename: string; mimeType: string; fileSize?: number; link?: string | null }[];
}

export interface AfspraakDatum {
  id: string;
  datum: string;
  vanUur: string | null;
  totUur: string | null;
}

export interface AfspraakPubliek {
  id: string;
  titel: string;
  beschrijving: string | null;
  status: string; // voorstel | bevestigd | geweigerd | verlopen
  bevestigdeDatum: string | null;
  createdAt: string;
  datums: AfspraakDatum[];
  trajectstap: {
    template: { naam: string; icon: string };
  };
  aanmaker: { firstName: string; lastName: string };
}

export interface DossierPubliek {
  id: string;
  dossierNummer: string;
  klantNaam: string;
  adres: string;
  postcode: string;
  stad: string;
  status: string;
  leveringsdatum: string | null;
  plaatsingsdatumStart: string | null;
  plaatsingsdatumEind: string | null;
  createdAt: string;
  updatedAt: string;
  entity: { name: string; phone: string | null; email: string | null };
  trajectstappen: TrajectstapPubliek[];
  apparaten: ApparaatPubliek[];
  documenten: DocumentPubliek[];
}

async function fetchPubliekDossier(api: string): Promise<DossierPubliek> {
  let res: Response;
  try {
    res = await fetch(`${api}`);
  } catch {
    throw new PortaalFout('Geen verbinding', null);
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new PortaalFout(data.message || 'Dossier niet gevonden', res.status);
  }
  return res.json();
}

async function fetchBerichten(api: string, fase?: string): Promise<BerichtPubliek[]> {
  const params = fase ? `?fase=${fase}` : '';
  const res = await fetch(`${api}/berichten${params}`);
  if (!res.ok) throw new Error('Berichten ophalen mislukt');
  return res.json();
}

async function sendBericht(api: string, content: string, naam?: string, fase?: string, fotos?: File[]): Promise<BerichtPubliek> {
  if (fotos && fotos.length > 0) {
    const formData = new FormData();
    formData.append('content', content);
    if (naam) formData.append('naam', naam);
    if (fase) formData.append('fase', fase);
    for (const foto of fotos) formData.append('fotos', foto);
    const res = await fetch(`${api}/berichten`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Bericht versturen mislukt');
    }
    return res.json();
  }
  const res = await fetch(`${api}/berichten`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, naam, fase }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Bericht versturen mislukt');
  }
  return res.json();
}

async function fetchAfspraken(api: string): Promise<AfspraakPubliek[]> {
  const res = await fetch(`${api}/afspraken`);
  if (!res.ok) throw new Error('Afspraken ophalen mislukt');
  return res.json();
}

export function usePubliekDossier(api: string) {
  return useQuery({
    queryKey: ['klantportaal', api],
    queryFn: () => fetchPubliekDossier(api),
    enabled: !!api,
    retry: false,
    refetchInterval: 30000,
  });
}

export function usePubliekBerichten(api: string, fase?: string) {
  return useQuery({
    queryKey: ['klantportaal', api, 'berichten', fase],
    queryFn: () => fetchBerichten(api, fase),
    enabled: !!api,
    refetchInterval: 15000,
  });
}

export function useSendPubliekBericht(api: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ content, naam, fase, fotos }: { content: string; naam?: string; fase?: string; fotos?: File[] }) =>
      sendBericht(api, content, naam, fase, fotos),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['klantportaal', api, 'berichten'] });
    },
  });
}

export function useMarkeerGelezen(api: string) {
  return useMutation({
    mutationFn: async (berichtId: string) => {
      const res = await fetch(`${api}/berichten/${berichtId}/gelezen`, {
        method: 'PUT',
      });
      if (!res.ok) throw new Error('Markeren als gelezen mislukt');
      return res.json();
    },
  });
}

export function usePubliekAfspraken(api: string) {
  return useQuery({
    queryKey: ['klantportaal', api, 'afspraken'],
    queryFn: () => fetchAfspraken(api),
    enabled: !!api,
  });
}

export function useBevestigAfspraak(api: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ afspraakId, datumId }: { afspraakId: string; datumId: string }) => {
      const res = await fetch(`${api}/afspraken/${afspraakId}/bevestigen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ datumId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Bevestigen mislukt');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['klantportaal', api, 'afspraken'] });
    },
  });
}

export interface BrandingPubliek {
  logoUrl: string | null;
  bedrijfsNaam: string | null;
  kleurPrimair: string | null;
  kleurSecundair: string | null;
  telefoon: string | null;
  email: string | null;
  website: string | null;
}

export function usePortaalBranding(api: string) {
  return useQuery<BrandingPubliek | null>({
    queryKey: ['klantportaal', api, 'branding'],
    queryFn: async () => {
      const res = await fetch(`${api}/branding`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!api,
    staleTime: 300000,
  });
}

export function useSubmitFeedback(api: string) {
  return useMutation({
    mutationFn: async ({ score, comment }: { score: number; comment?: string }) => {
      const res = await fetch(`${api}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, comment }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message || 'Feedback versturen mislukt');
      }
      return res.json();
    },
  });
}

export function useSubmitSchade(api: string) {
  return useMutation({
    mutationFn: async ({ beschrijving, naam, fotos }: { beschrijving: string; naam?: string; fotos?: File[] }) => {
      const formData = new FormData();
      formData.append('beschrijving', beschrijving);
      if (naam) formData.append('naam', naam);
      if (fotos) {
        for (const foto of fotos) formData.append('fotos', foto);
      }
      const res = await fetch(`${api}/schade`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message || 'Schade melden mislukt');
      }
      return res.json();
    },
  });
}

export function useWeigerAfspraak(api: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (afspraakId: string) => {
      const res = await fetch(`${api}/afspraken/${afspraakId}/weigeren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Weigeren mislukt');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['klantportaal', api, 'afspraken'] });
    },
  });
}

export interface ActiviteitPubliek {
  id: string;
  type: string;
  tijdstip: string;
  tekst: string;
  icon?: string;
}

export function usePubliekActiviteiten(api: string) {
  return useQuery<ActiviteitPubliek[]>({
    queryKey: ['klantportaal', api, 'activiteiten'],
    queryFn: async () => {
      const res = await fetch(`${api}/activiteiten`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!api,
    refetchInterval: 30000,
  });
}
